import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StarIcon as Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";

/**
 * Customer-facing review modal. Triggered from `/my-bookings` for a
 * `completed` booking. Calls POST /providers/{provider_id}/reviews —
 * the backend gates on (a) booking is theirs, (b) booking is completed,
 * (c) not already reviewed.
 */
export default function LeaveReviewDialog({ open, onOpenChange, booking, onSubmitted }) {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!booking) return;
    setBusy(true);
    try {
      await api.post(`/providers/${booking.provider_id}/reviews`, {
        booking_id: booking.id,
        rating,
        text,
      });
      toast.success("Thanks for your review!");
      onSubmitted?.();
      onOpenChange(false);
      // Reset for next open
      setRating(5);
      setText("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const display = hover || rating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl">How was your service?</DialogTitle>
          <DialogDescription>
            {booking?.service_name ? <>Reviewing <strong>{booking.service_name}</strong>. </> : null}
            Your review will be public on the provider's profile.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-4">
          <div className="flex items-center justify-center gap-1 py-2" data-testid="rating-stars">
            {[1, 2, 3, 4, 5].map((i) => (
              <button
                key={i}
                type="button"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setRating(i)}
                aria-label={`${i} star${i === 1 ? "" : "s"}`}
                data-testid={`star-${i}`}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star
                  size={32}
                  weight={display >= i ? "fill" : "regular"}
                  className={display >= i ? "text-amber-400" : "text-[#D8D5CC]"}
                />
              </button>
            ))}
          </div>
          <Textarea
            rows={4}
            placeholder="Share what stood out — punctuality, communication, results…"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 2000))}
            data-testid="review-text"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="review-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="rounded-full bg-ink hover:bg-crimson text-white"
            data-testid="review-submit"
          >
            {busy ? "Posting…" : "Post review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
